import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

/**
 * Language option with code (for API) and label (for display)
 */
export interface LanguageOption {
    code: string;
    label: string;
}

/**
 * Static list of supported languages (alphabetical by label).
 * Reusable for Prompt Creation dialog, project creation, and elsewhere.
 */
/**
 * Supported languages for prompt generation and translation.
 * Includes all major languages spoken in countries from CountryService,
 * supported by LLM and web search (e.g. Indian languages, Spanish regional, South African, etc.).
 */
const LANGUAGE_DATA: LanguageOption[] = [
    { code: 'af', label: 'Afrikaans' },
    { code: 'ar', label: 'Arabic' },
    { code: 'eu', label: 'Basque' },
    { code: 'bn', label: 'Bengali' },
    { code: 'yue', label: 'Cantonese' },
    { code: 'ca', label: 'Catalan' },
    { code: 'cs', label: 'Czech' },
    { code: 'da', label: 'Danish' },
    { code: 'nl', label: 'Dutch' },
    { code: 'en', label: 'English' },
    { code: 'et', label: 'Estonian' },
    { code: 'fi', label: 'Finnish' },
    { code: 'fr', label: 'French' },
    { code: 'gl', label: 'Galician' },
    { code: 'de', label: 'German' },
    { code: 'gu', label: 'Gujarati' },
    { code: 'he', label: 'Hebrew' },
    { code: 'hi', label: 'Hindi' },
    { code: 'it', label: 'Italian' },
    { code: 'ja', label: 'Japanese' },
    { code: 'kn', label: 'Kannada' },
    { code: 'ko', label: 'Korean' },
    { code: 'ms', label: 'Malay' },
    { code: 'ml', label: 'Malayalam' },
    { code: 'zh', label: 'Mandarin Chinese' },
    { code: 'mr', label: 'Marathi' },
    { code: 'no', label: 'Norwegian' },
    { code: 'pl', label: 'Polish' },
    { code: 'pt', label: 'Portuguese' },
    { code: 'pa', label: 'Punjabi' },
    { code: 'es', label: 'Spanish' },
    { code: 'sv', label: 'Swedish' },
    { code: 'ta', label: 'Tamil' },
    { code: 'te', label: 'Telugu' },
    { code: 'ur', label: 'Urdu' },
    { code: 'cy', label: 'Welsh' },
    { code: 'xh', label: 'Xhosa' },
    { code: 'zu', label: 'Zulu' }
];

/** Default language code (English) */
export const DEFAULT_LANGUAGE_CODE = 'en';

@Injectable({
    providedIn: 'root'
})
export class LanguageService {

    /**
     * Get language options for dropdowns, sorted alphabetically by label.
     * Reusable for Prompt Creation dialog, project creation, etc.
     */
    getLanguageOptions(): Observable<LanguageOption[]> {
        return of([...LANGUAGE_DATA].sort((a, b) => a.label.localeCompare(b.label)));
    }

    /**
     * Get language options synchronously (alphabetical).
     */
    getLanguageOptionsSync(): LanguageOption[] {
        return [...LANGUAGE_DATA].sort((a, b) => a.label.localeCompare(b.label));
    }

    /**
     * Get display label for a language code.
     */
    getLanguageLabel(code: string): string {
        const option = LANGUAGE_DATA.find(l => l.code === code);
        return option?.label ?? code;
    }

    /**
     * Get default language code (English).
     */
    getDefaultLanguageCode(): string {
        return DEFAULT_LANGUAGE_CODE;
    }
}
